import { useEffect, useRef, useState } from "react";
import { useAsync } from "../useAsync.js";
import { dayKey, formatDate, formatTime, isoDay } from "../format.js";
import { ActionConfirmation } from "../ui/ActionConfirmation.js";
import {
  booking,
  type StudentLesson,
  type AvailabilitySlot,
} from "../booking/api.js";
import { describeCredit } from "../booking/policy.js";
import { timeRequestClosesAt, timeRequestOfferClosesAt } from "@wmf/shared/waitlist";
import { waitlistApi, type RequestableTime } from "./api.js";

export function RequestTimeEntry(props: {
  studentId: string;
  tutorId: string;
  serviceId: number;
  providerId: number;
  serviceName: string;
  lesson?: StudentLesson;
  onChanged: () => void;
  onFree: (slot: AvailabilitySlot) => void;
  label?: string;
  initialDate?: string;
}) {
  const config = useAsync(() => waitlistApi.config(), []);
  const [open, setOpen] = useState(false);
  if (!config.data?.enabled || !props.tutorId) return null;
  return (
    <div style={{ marginTop: 12 }}>
      <button
        className="quiet"
        type="button"
        onClick={() => setOpen((value) => !value)}
      >
        {open
          ? "Close time request"
          : (props.label ?? "Request a specific time")}
      </button>
      {open && <RequestTimePicker {...props} />}
    </div>
  );
}
export function MoveTimeRequestEntry(props: {
  studentId: string;
  lesson: StudentLesson;
  onChanged: () => void;
  onFree: (slot: AvailabilitySlot) => void;
}) {
  const config = useAsync(() => waitlistApi.config(), []);
  if (
    !config.data?.enabled ||
    !props.lesson.enrichment?.actions.reschedule.allowed
  )
    return null;
  return <EnabledMoveTimeRequestEntry {...props} />;
}

function EnabledMoveTimeRequestEntry({
  studentId,
  lesson,
  onChanged,
  onFree,
}: {
  studentId: string;
  lesson: StudentLesson;
  onChanged: () => void;
  onFree: (slot: AvailabilitySlot) => void;
}) {
  const offering = useAsync(() => booking.offerings(studentId), [studentId]);
  const pair = offering.data?.offerings?.find(
    (row) =>
      row.provider_id === lesson.providerId &&
      row.service_id === lesson.serviceId,
  );
  if (!lesson.enrichment?.actions.reschedule.allowed || !pair?.tutor_id)
    return null;
  return (
    <RequestTimeEntry
      key={`move:${studentId}:${lesson.bookingId}`}
      studentId={studentId}
      tutorId={pair.tutor_id}
      serviceId={pair.service_id}
      providerId={pair.provider_id}
      serviceName={lesson.serviceName ?? "Lesson"}
      lesson={lesson}
      initialDate={dayKey(lesson.startsAt)}
      onChanged={onChanged}
      onFree={onFree}
      label="None of these work — request a specific time"
    />
  );
}
/** A taken time, or a free one too close to book directly, is requested. */
function requestable(time: RequestableTime) {
  return time.state === "taken" || time.subjectToApproval;
}
export function RequestTimePicker({
  studentId,
  tutorId,
  serviceId,
  providerId,
  serviceName,
  lesson,
  initialDate,
  onChanged,
  onFree,
}: {
  studentId: string;
  tutorId: string;
  serviceId: number;
  providerId: number;
  serviceName: string;
  lesson?: StudentLesson;
  initialDate?: string;
  onChanged: () => void;
  onFree: (slot: AvailabilitySlot) => void;
}) {
  const validInitialDate = initialDate && /^\d{4}-\d{2}-\d{2}$/.test(initialDate)
    ? initialDate
    : isoDay(0);
  const dateWasChosen = useRef(false);
  const [date, setDate] = useState(validInitialDate);
  useEffect(() => {
    if (!dateWasChosen.current) setDate(validInitialDate);
  }, [validInitialDate]);
  const times = useAsync(
    () =>
      waitlistApi.requestableTimes(
        tutorId,
        serviceId,
        studentId,
        date,
        lesson?.bookingId,
      ),
    [studentId, tutorId, serviceId, date, lesson?.bookingId],
  );
  const preview = useAsync(
    () =>
      lesson
        ? booking.cancellationPreview(
            studentId,
            lesson.bookingId,
            "Scheduled by lesson time request",
          )
        : Promise.resolve(null),
    [studentId, lesson?.bookingId],
  );
  const [selected, setSelected] = useState<RequestableTime | null>(null);
  const [fallback, setFallback] = useState<"keep" | "cancel">("keep");
  const [busy, setBusy] = useState(false),
    [error, setError] = useState<string | null>(null),
    [done, setDone] = useState<string | null>(null);
  const deadlineRequest = {
    purpose: "move",
    actBy:
      lesson && preview.data
        ? new Date(
            new Date(preview.data.starts_at).getTime() -
              preview.data.notice_required_seconds * 1000,
          )
        : null,
    requestedStartsAt: selected ? new Date(selected.startsAt) : null,
  };
  const closesAt = timeRequestClosesAt(deadlineRequest)?.toISOString() ?? null;
  const offerClosesAt =
    timeRequestOfferClosesAt(deadlineRequest)?.toISOString() ?? null;
  async function chooseFree() {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      const nextDay = new Date(`${date}T12:00:00Z`);
      nextDay.setUTCDate(nextDay.getUTCDate() + 1);
      const available = await booking.availability(studentId, {
        provider_id: String(providerId),
        service_id: String(serviceId),
        start_date: date,
        end_date: nextDay.toISOString().slice(0, 10),
      });
      const slot = available.slots.find(
        (row) =>
          new Date(row.starts_at).getTime() ===
          new Date(selected.startsAt).getTime(),
      );
      if (!slot) {
        setError("That time is no longer available. Please choose again.");
        return;
      }
      onFree(slot);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Could not check this free time.",
      );
    } finally {
      setBusy(false);
    }
  }
  async function submit() {
    if (!selected || !requestable(selected)) return;
    setBusy(true);
    setError(null);
    try {
      const base = {
        kind: "specific_time" as const,
        studentId,
        tutorId,
        serviceId,
        serviceName,
        startsAt: selected.startsAt,
      };
      const result = await waitlistApi.createTimeRequest(
        lesson
          ? { ...base, purpose: "move", bookingId: lesson.bookingId, fallback }
          : { ...base, purpose: "book" },
      );
      setDone(
        result.subjectToApproval
          ? `Requested ${formatDate(selected.startsAt)} at ${formatTime(selected.startsAt)}, subject to tutor approval because it is within 24 hours. Your tutor has been told. We will email you when they have approved it and it is available, so you can accept. Nothing is booked or moved before then.`
          : `We'll email you if ${formatTime(selected.startsAt)} on ${formatDate(selected.startsAt)} becomes free.`,
      );
      onChanged();
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "The request could not be saved.",
      );
    } finally {
      setBusy(false);
    }
  }
  if (done)
    return (
      <ActionConfirmation
        variant="success"
        title="Lesson time requested"
        description={done}
        reveal
      />
    );
  return (
    <div className="panel" style={{ marginTop: 10 }}>
      <p className="small muted">
        Choose a time within your tutor's working hours. Requests within 24
        hours are subject to tutor approval.
      </p>
      <label className="stack">
        Date
        <input
          type="date"
          min={isoDay(0)}
          value={date}
          disabled={busy}
          onChange={(event) => {
            dateWasChosen.current = true;
            setDate(event.target.value);
            setSelected(null);
            setError(null);
          }}
        />
      </label>
      {times.loading ? (
        <p role="status">Checking lesson times…</p>
      ) : times.error ? (
        <p className="error" role="alert">
          {times.error}
        </p>
      ) : (
        <div
          className="slot-grid"
          role="group"
          aria-label="Requestable lesson times"
        >
          {(times.data?.times ?? []).map((time) => (
            <button
              className="quiet"
              type="button"
              key={time.startsAt}
              aria-pressed={selected?.startsAt === time.startsAt}
              disabled={busy}
              onClick={() => setSelected(time)}
            >
              {formatTime(time.startsAt)} ·{" "}
              {time.state === "taken"
                ? time.subjectToApproval
                  ? "taken — request it, subject to approval"
                  : "taken — request it"
                : time.subjectToApproval
                  ? "free — request it, subject to approval"
                  : "free — book now"}
            </button>
          ))}
          {!times.data?.times.length && (
            <p>No requestable times within working hours on this date.</p>
          )}
        </div>
      )}
      {selected?.state === "free" && !selected.subjectToApproval && (
        <p className="small">
          <button
            type="button"
            disabled={busy}
            onClick={() => void chooseFree()}
          >
            {lesson ? "Move to this free time" : "Choose this free time"}
          </button>
        </p>
      )}
      {selected && requestable(selected) && (
        <>
          {selected.subjectToApproval && (
            <p className="notice">
              {selected.state === "free"
                ? "This time is free but within 24 hours. Your tutor will be asked to approve it. Nothing is booked or moved unless they approve and you then accept."
                : "Subject to tutor approval — this time is within 24 hours. Nothing is booked or moved unless your tutor approves and you then accept."}
            </p>
          )}
          {lesson && (
            <fieldset disabled={busy}>
              <legend>If the requested move is not accepted</legend>
              <label>
                <input
                  type="radio"
                  name="time-request-fallback"
                  checked={fallback === "keep"}
                  onChange={() => setFallback("keep")}
                  required
                />{" "}
                Keep my lesson on {formatDate(lesson.startsAt)} at{" "}
                {formatTime(lesson.startsAt)}
              </label>
              <label>
                <input
                  type="radio"
                  name="time-request-fallback"
                  checked={fallback === "cancel"}
                  disabled={
                    !preview.data?.notice_satisfied ||
                    !preview.data.cancellation.allowed ||
                    preview.data.credit.effect === "forfeit"
                  }
                  onChange={() => setFallback("cancel")}
                  required
                />{" "}
                Cancel my current lesson unless the requested move is accepted
              </label>
              {offerClosesAt && (
                <p className="small">
                  Please accept a move by {formatDate(offerClosesAt)} at{" "}
                  {formatTime(offerClosesAt)}.
                  {fallback === "cancel" && closesAt && (
                    <>
                      {" "}
                      If it has not been accepted, we will cancel your current
                      lesson on {formatDate(closesAt)} at {formatTime(closesAt)}.{" "}
                      {preview.data && describeCredit(preview.data.credit)}
                    </>
                  )}
                </p>
              )}
              {preview.error && <p className="error">{preview.error}</p>}
            </fieldset>
          )}
          <button
            type="button"
            disabled={busy || (!!lesson && !closesAt)}
            onClick={() => void submit()}
          >
            {busy ? "Saving…" : "Request this time"}
          </button>
        </>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
